<template>
  <v-sheet
    class="custom-calendar-container custom-month-calendar-container fill-height is-relative">
    <div class="d-flex align-center pb-2">
      <zoom-buttons></zoom-buttons>
    </div>

    <FullCalendar
      :class="`calendar-view-${$viewType.toLowerCase()}`"
      ref="fullCalendar"
      :options="localCalendarOptions"
      :data-zoom-level="$zoomLevel"
      :data-interval-setting="$intervalMinutes">
      <!-- Day cell content when feature flag is enabled -->
      <template #day-cell-content="props">
        <div class="text-right mb-2">{{ props.dayNumberText }}</div>
        <div class="status-count-container" v-if="isGroupEventsByStatusEnabled.enabled">
          <span
            v-for="statusItem in getDayStatusCounts(props)"
            :key="statusItem.status"
            class="status-count-item d-flex align-center"
            :class="`bg-${statusItem.status.toLowerCase()}`">
            <span class="pr-2">{{ statusItem.count }}</span>
            <span class="status-name">{{ statusItem.status }}</span>
          </span>
        </div>
      </template>

      <template #event-content="props">
        <div
          v-if="props.event.display !== 'background' && !isGroupEventsByStatusEnabled.enabled"
          class="event-content"
          :key="`${renderKey}-${props.event.extendedProps.id}`"
          :data-weekday="props.event.extendedProps.weekday"
          :data-event-id="props.event.id"
          :data-status="props.event.extendedProps.status.toLowerCase()"
          :data-time="props.event.extendedProps.startTime24.replace(/^0/, '')"
          :data-date="props.event.extendedProps.startDate"
          :data-dock-id="props.event.extendedProps.dockId">
          <event-with-tooltip
            :is-light-theme="$useLightGridTheme"
            :event="{ ...props.event.toPlainObject(), ...props.event.extendedProps }"
            :enableMilitaryTime="$isMilitaryTimeEnabled($selectedWarehouse)"
            :view-type="CalendarViewEnum.MONTH"
            :use-new-grid-tiles="useNewGridTilesSetting"
            @click="handleEventClick"
            @cancel-reserve="cancelReserve"
            :disable-tooltip="isTooltipDisabled(props.event.extendedProps)"></event-with-tooltip>
        </div>

        <div
          v-if="
            isStartingStatus(props.event.extendedProps.status) &&
            $rolePermissions.canUpdateAppointment
          "
          class="v-event-drag-bottom"
          @mousedown.stop="extendBottom(props.event.extendedProps)"></div>
      </template>
    </FullCalendar>

    <calendar-dialogs
      :appointment-context="appointmentContext"
      :show-create-dialog="showCreateDialog"
      :show-details-dialog="showDetailsDialog"
      :show-reserve-dialog="showReserveDialog"
      @close="handleDialogClose" />

    <event-context-menu
      v-if="eventContextMenuEvent"
      @close="clearContextMenuData"
      :nativeEvent="eventContextMenuEvent"
      :appointment="{
        ...localEvents.find(event => event.id === eventContextMenuId)
      }" />
  </v-sheet>
</template>

<script>
import calendarMixin from '@/modules/calendar/mixins/calendarMixin';
import { CalendarViewEnum } from '@/enums';
import FullCalendar from '@fullcalendar/vue3';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import luxonPlugin from '@fullcalendar/luxon2';
import { DateTime } from 'luxon';
import { useFlag } from '@loadsmart/vue-feature-flags';
import { ref } from 'vue';

/**
 * Calendar Day View
 * @displayName Calendar Day View
 */
export default {
  name: 'CalendarDayView',
  mixins: [calendarMixin],
  components: {
    FullCalendar
  },
  setup() {
    const isGroupEventsByStatusEnabled = useFlag('group-events-by-status-month-view');
    const shouldShowEvents = ref(false);

    return { isGroupEventsByStatusEnabled, shouldShowEvents };
  },
  computed: {
    localCalendarOptions() {
      return {
        ...this.calendarOptions,
        events: this.shouldShowEvents ? this.localEvents : [],
        dayMaxEventRows: 1,
        dayMaxEvents: 1,
        initialView: 'dayGridMonth',
        plugins: [luxonPlugin, dayGridPlugin, interactionPlugin],

        moreLinkClick: info => {
          const dateTime = DateTime.fromFormat(
            `${info.date.getFullYear()}-${info.date.getMonth() + 1}-${info.date.getDate()}`,
            'yyyy-M-d',
            { zone: this.timezone }
          );

          this.$store.dispatch('Calendar/setSelectedDate', {
            value: dateTime.minus({ minutes: dateTime.offset }).toISO(),
            getEvents: false
          });
          this.$store.dispatch('Calendar/setViewType', {
            value: CalendarViewEnum.DAY.toUpperCase(),
            getEvents: true
          });
          return false;
        },
        viewDidMount: () => {
          this.ready = true;
        }
      };
    },
    localEvents() {
      return this.events;
    },
    localEventsByDateAndStatus() {
      const groupedEvents = {};

      this.localEvents.forEach(event => {
        const eventDate = DateTime.fromISO(event.start).setZone(this.timezone).toISODate();

        const category =
          event.type === this.novaCore.AppointmentType.Reserve
            ? this.novaCore.AppointmentType.Reserve
            : event.status;

        // Initialize date group if it doesn't exist
        if (!groupedEvents[eventDate]) {
          groupedEvents[eventDate] = {};
        }

        // Initialize status group if it doesn't exist
        if (!groupedEvents[eventDate][category]) {
          groupedEvents[eventDate][category] = [];
        }

        // Add event to the appropriate status group
        groupedEvents[eventDate][category].push(event);
      });

      return groupedEvents;
    }
  },
  methods: {
    handleCalendarClick(dateClickInfo) {
      this.$store.dispatch('Calendar/setSelectedDate', {
        value: DateTime.fromJSDate(dateClickInfo.date).setZone('utc').toISO(),
        getEvents: true
      });

      this.$store.dispatch('Calendar/setViewType', { value: CalendarViewEnum.DAY.toUpperCase() });
    },
    getDayStatusCounts({ date }) {
      const eventDate = DateTime.fromJSDate(date).setZone(this.timezone).toISODate();
      const events = this.localEventsByDateAndStatus[eventDate];

      if (!events) {
        return {};
      }

      const statusOrder = [
        this.novaCore.AppointmentStatus.Requested,
        this.novaCore.AppointmentStatus.Scheduled,
        this.novaCore.AppointmentStatus.Arrived,
        this.novaCore.AppointmentStatus.InProgress,
        this.novaCore.AppointmentStatus.Completed,
        this.novaCore.AppointmentStatus.NoShow,
        this.novaCore.AppointmentStatus.Cancelled,
        this.novaCore.AppointmentType.Reserve
      ];

      const statusItems = Object.keys(events).map(status => ({
        status,
        count: events[status].length
      }));

      return statusItems.sort((a, b) => {
        const indexA = statusOrder.indexOf(a.status);
        const indexB = statusOrder.indexOf(b.status);
        return indexA - indexB;
      });
    }
  },
  async mounted() {
    this.calendarApi.gotoDate(this.$selectedDate);
    this.mounted = true;
  },
  watch: {
    isGroupEventsByStatusEnabled: {
      immediate: true,
      handler(value) {
        const enabled = Boolean(value?.enabled);
        this.shouldShowEvents = !enabled;
        if (!enabled) {
          // If flag is false, we need to refetch events with correct data
          this.$store.dispatch('Calendar/getEvents');
        }
      }
    },
    $selectedDate() {
      this.calendarApi.gotoDate(this.$selectedDate);
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.fc-more-popover) {
  display: none;
}

:deep(.fc-event) {
  height: 30px;

  .event-container {
    padding: 0 10px;
  }
}

:deep(.fc-daygrid-day-top) {
  flex-direction: row;

  a {
    width: 100%;
  }
}

.status-count-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 6px;
  padding: 2px;
}

.status-count-item {
  display: flex;
  font-size: 0.6rem;
  padding: 1px 6px;
  border-radius: 4px;
}
</style>
